import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import PaymentIcon from '@mui/icons-material/Payment'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import MicIcon from '@mui/icons-material/Mic'
import CampaignIcon from '@mui/icons-material/Campaign'
import { useAuth } from '../App'
import StripePaymentForm from './StripePaymentForm'
import { useTranslation } from 'react-i18next'
import { tokens } from '../theme/tokens'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

interface BillingModalProps {
  open: boolean
  onClose: () => void
}

interface BackendTransaction {
  id: number
  wallet_id: number
  amount: number // in microeuros
  type: 'credit' | 'debit'
  description: string
  created_at: string
}

interface PaginatedTransactionsResponse {
  data: BackendTransaction[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasMore: boolean
  }
}

interface Transaction {
  id: string
  date: string
  amount: number // in euros
  type: 'payment' | 'usage'
  description: string
  invoice_url?: string
}

interface WalletBalance {
  balance: number // in microeuros
  balanceEuros: number // in euros
  currency?: 'EUR'
}

const BillingModal: React.FC<BillingModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [loading, setLoading] = useState(false)
  const [topupAmount, setTopupAmount] = useState<string>('10')
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [error, setError] = useState<string | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [totalTransactions, setTotalTransactions] = useState(0)
  const auth = useAuth()

  useEffect(() => {
    if (open) {
      // Reset pagination when modal opens
      setCurrentPage(1)
      setTransactions([])
      fetchBillingData(1, true)
    }
  }, [open])

  const fetchBillingData = async (page: number = 1, reset: boolean = false) => {
    if (reset) {
      setLoading(true)
      setError(null)
    } else {
      setLoadingMore(true)
    }

    try {
      const token = await auth.getToken()

      // Fetch wallet balance (from apirest-v3 directly) - only on first load
      if (reset || !walletBalance) {
        const balanceResponse = await fetch(`${API_BASE}/wallet/firebase/balance`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        if (balanceResponse.ok) {
          const balanceData = await balanceResponse.json()
          setWalletBalance(balanceData)
        }
      }

      // Fetch transactions with pagination
      const transactionsResponse = await fetch(`${API_BASE}/wallet/firebase/transactions?page=${page}&limit=25`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (transactionsResponse.ok) {
        const responseData: PaginatedTransactionsResponse = await transactionsResponse.json()

        // Convert backend transaction format to frontend format
        const convertedTransactions: Transaction[] = responseData.data.map(tx => ({
          id: tx.id.toString(),
          date: tx.created_at,
          amount: tx.amount / 1_000_000, // Convert from microeuros to euros
          type: tx.type === 'credit' ? 'payment' : 'usage',
          description: tx.description
        }))

        if (reset) {
          setTransactions(convertedTransactions)
        } else {
          // Append new transactions
          setTransactions(prev => [...prev, ...convertedTransactions])
        }

        setCurrentPage(page)
        setHasMore(responseData.pagination.hasMore)
        setTotalTransactions(responseData.pagination.total)
      }

    } catch (err) {
      setError('Failed to load billing data')
      console.error('Billing data fetch error:', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const handleLoadMore = async () => {
    if (!loadingMore && hasMore) {
      await fetchBillingData(currentPage + 1, false)
    }
  }

  const handleTopup = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      setError('Please enter a valid amount')
      return
    }

    if (parseFloat(topupAmount) < 0.50) {
      setError('Minimum top-up amount is €0.50')
      return
    }

    if (parseFloat(topupAmount) > 10000) {
      setError('Maximum top-up amount is €10,000')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await auth.getToken()

      // Create payment intent via Stripe
      const response = await fetch(`${API_BASE}/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amountEuros: parseFloat(topupAmount)
        })
      })

      if (response.ok) {
        const { client_secret } = await response.json()

        // Show the embedded Stripe payment form
        setPaymentClientSecret(client_secret)
        setShowPaymentForm(true)
        setError(null)

      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Payment intent creation failed')
      }
    } catch (err) {
      setError('Network error occurred')
      console.error('Payment error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadInvoice = (invoiceUrl: string) => {
    window.open(invoiceUrl, '_blank')
  }

  const handlePaymentSuccess = async () => {
    setShowPaymentForm(false)
    setPaymentClientSecret(null)
    setTopupAmount('10')

    // Refresh billing data after payment success
    await fetchBillingData(1, true)
  }

  const handlePaymentCancel = () => {
    setShowPaymentForm(false)
    setPaymentClientSecret(null)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="billing-dialog-title">
      <DialogTitle id="billing-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <AccountBalanceWalletIcon sx={{ mr: 1 }} aria-hidden="true" />
            <Typography variant="h6">{t('billingPayments')}</Typography>
          </Box>
          <IconButton onClick={onClose} aria-label={t('common.close', 'Close')}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Wallet Balance Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('currentBalance')}
            </Typography>
            {loading && !walletBalance ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Typography variant="h4" color="primary">
                  €{walletBalance?.balanceEuros?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('sharedBalance', 'Shared balance for ASR and TTS services')}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* Top-up Section or Payment Form */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {showPaymentForm && paymentClientSecret ? (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('completePayment')}
                </Typography>
                <StripePaymentForm
                  clientSecret={paymentClientSecret}
                  amount={parseFloat(topupAmount)}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                />
              </>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('addFunds')}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={`${t('amount')} (EUR)`}
                      type="number"
                      value={topupAmount}
                      onChange={(e) => setTopupAmount(e.target.value)}
                      inputProps={{ min: 0.50, max: 10000, step: 0.01 }}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<PaymentIcon />}
                      onClick={handleTopup}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : t('addFunds')}
                    </Button>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('minimumAmount')}, {t('maximumAmount')}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pricing Cards */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('pricing', 'Pricing')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  borderLeft: `3px solid ${tokens.colors.primary.main}`
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <MicIcon sx={{ color: tokens.colors.primary.darkText }} />
                    <Typography variant="subtitle1" fontWeight={600}>{t('asrPricing', 'ASR - Speech to Text')}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{t('perMinuteRate', '€0.01 per minute of audio')}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  borderLeft: `3px solid ${tokens.colors.tts.main}`
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CampaignIcon sx={{ color: tokens.colors.tts.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>{t('ttsPricing', 'TTS - Text to Speech')}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{t('standardVoiceRate', '€0.01 per 1,000 characters (standard)')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('clonedVoiceRate', '€0.03 per 1,000 characters (cloned)')}</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Divider sx={{ my: 2 }} />

        {/* Transaction History */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            {t('transactionHistory')}
          </Typography>
          {totalTransactions > 0 && (
            <Typography variant="caption" color="text.secondary">
              {transactions.length} of {totalTransactions} {t('billing.transactions', 'transactions')}
            </Typography>
          )}
        </Box>

        {loading && transactions.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : transactions.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            {t('noTransactions')}
          </Typography>
        ) : (
          <>
            {/* Mobile: card stack layout */}
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {transactions.map((transaction) => (
                  <Card key={transaction.id} variant="outlined">
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(transaction.date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                            {transaction.description}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 700 }}
                            color={transaction.type === 'payment' ? 'success.main' : 'error.main'}
                          >
                            {transaction.type === 'payment' ? '+' : '-'}€{transaction.amount.toFixed(2)}
                          </Typography>
                          {transaction.invoice_url && (
                            <IconButton
                              size="small"
                              aria-label={t('downloadInvoice', 'Download invoice')}
                              onClick={() => handleDownloadInvoice(transaction.invoice_url!)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Box mt={0.75}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: transaction.type === 'payment' ? 'success.light' : 'warning.light',
                            color: transaction.type === 'payment' ? 'success.dark' : 'warning.dark'
                          }}
                        >
                          {transaction.type === 'payment' ? t('payment') : t('usage')}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              /* Desktop: table layout */
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('date')}</TableCell>
                      <TableCell>{t('description')}</TableCell>
                      <TableCell>{t('type')}</TableCell>
                      <TableCell align="right">{t('amount')}</TableCell>
                      <TableCell align="center">{t('invoice')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {new Date(transaction.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: transaction.type === 'payment' ? 'success.light' : 'warning.light',
                              color: transaction.type === 'payment' ? 'success.dark' : 'warning.dark'
                            }}
                          >
                            {transaction.type === 'payment' ? t('payment') : t('usage')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={transaction.type === 'payment' ? 'success.main' : 'error.main'}
                          >
                            {transaction.type === 'payment' ? '+' : '-'}€{transaction.amount.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {transaction.invoice_url && (
                            <IconButton
                              size="small"
                              aria-label={t('downloadInvoice', 'Download invoice')}
                              onClick={() => handleDownloadInvoice(transaction.invoice_url!)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {hasMore && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                >
                  {loadingMore ? t('billing.loading', 'Loading...') : t('billing.loadMore', 'Load More')}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default BillingModal
